import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import {
  AgentThreadRecord,
  AgentThreadRole,
  AgentThreadStatus,
  AgentThreadTextContent,
  ClientToolSpec,
  ClientViewingContext,
  InvokeSkillSpec,
} from "@/shared/domain/agentThread";
import {
  activeAgentThreadQueryKey,
  activeAgentThreadQueryKeyPrefix,
} from "@/shared/domain/agentThread/hooks/useActiveAgentThread";

interface ExistingChatArgs {
  chatId: string;
  /** One or more skills to chip into the same send. Resolved + fabricated in order. */
  requests: InvokeSkillSpec[];
  /** Optional message text to send alongside the chips. */
  message?: string;
}

interface NewChatArgs {
  /** Sentinel: invoke the skills in a brand-new thread. */
  chatId: null;
  requests: InvokeSkillSpec[];
  /** Optional message text to seed the new thread alongside the chips. */
  message?: string;
  /** Forwarded to the new thread so the model sees the user's dataset/file
   * selection from the input pane — same trio useStartAgentThread sends. */
  clientContext?: ClientViewingContext;
  modelProfile?: string;
  clientTools?: ClientToolSpec[];
}

type Args = ExistingChatArgs | NewChatArgs;

interface InvokeSkillResult {
  /** Populated only when the mutation created a new thread (chatId was null). */
  newChat?: AgentThreadRecord;
}

/**
 * Invoke one or more skills into an agent thread.
 *
 * When `chatId` is provided the skills are added to that session via the
 * standard send-message endpoint with `invoke_skills` set. When `chatId` is
 * `null` the mutation creates a new session whose first action is the
 * fabricated tool_use / tool_result pairs, returning the new AgentThreadRecord
 * so the caller can wire it as the active thread.
 *
 * On success the cached AgentThreadRecord's status is flipped to ROBOTO_TURN so
 * useActiveAgentThread starts polling — without this, polling stays off (it's
 * USER_TURN before the call) and the freshly-written fabricated blocks don't
 * show up in the UI until the user manually refreshes.
 */
export function useInvokeSkill(orgId: string | undefined) {
  const { agentThreadService, skillService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<InvokeSkillResult, Error, Args>({
    mutationFn: async (args) => {
      if (args.requests.length === 0) {
        throw Error("useInvokeSkill requires at least one skill request.");
      }
      if (args.chatId === null) {
        if (orgId === undefined) {
          throw Error(
            "useInvokeSkill requires an Org ID for new-thread starts.",
          );
        }
        const newChat = await agentThreadService.startAgentThread(
          args.message,
          {
            orgId,
            invokeSkills: args.requests.map((r) => ({
              skill_id: r.skill_id,
              version: r.version,
            })),
            clientContext: args.clientContext,
            modelProfile: args.modelProfile,
            clientTools: args.clientTools,
          },
        );
        return { newChat };
      }
      const textMessage = args.message?.trim();
      await skillService.invokeSkillsInChat(args.chatId, args.requests, {
        orgId,
        message: textMessage
          ? {
              role: AgentThreadRole.USER,
              content: [{ text: textMessage } as AgentThreadTextContent],
            }
          : undefined,
      });
      return {};
    },
    onSuccess: async (result, args) => {
      void queryClient.invalidateQueries({
        queryKey: ["agentThreadsForUser"],
      });

      if (args.chatId !== null) {
        const queryKey = activeAgentThreadQueryKeyPrefix(args.chatId);
        await queryClient.cancelQueries({ queryKey });
        const cached = queryClient.getQueriesData<AgentThreadRecord>({
          queryKey,
        });
        for (const [cachedQueryKey, previousChat] of cached) {
          if (previousChat === undefined) {
            continue;
          }
          queryClient.setQueryData<AgentThreadRecord>(cachedQueryKey, {
            ...previousChat,
            status: AgentThreadStatus.ROBOTO_TURN,
          });
        }
        return;
      }

      if (result.newChat !== undefined) {
        // Seed both loadMessages variants — the record already has messages,
        // so the loadMessages=false cache slot can hold it without harm.
        // Without the second seed, a downstream consumer mounting
        // useActiveAgentThread with loadMessages=false would miss the
        // prefilled record entirely.
        const sessionId = result.newChat.session_id;
        queryClient.setQueryData<AgentThreadRecord>(
          activeAgentThreadQueryKey(sessionId, true),
          result.newChat,
        );
        queryClient.setQueryData<AgentThreadRecord>(
          activeAgentThreadQueryKey(sessionId, false),
          result.newChat,
        );
      }
    },
  });
}
